import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useTheme } from "../lib/theme";
import { api } from "../lib/api";

const SettingsCtx = createContext({ settings: null, refresh: () => {} });

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/public/branding");
      setSettings(data);
      if (typeof document !== "undefined") {
        document.title = `eneprox ${data.org_name || "Flow"} — Solar PV Operations`;
        // Apply org branding to CSS custom properties on <html> so it takes
        // effect across every component that reads `var(--accent)` etc. —
        // including the landing hero, login screen, buttons and chart tint.
        const root = document.documentElement;
        if (data.accent_color) {
          root.style.setProperty("--accent", data.accent_color);
          root.style.setProperty("--accent-hover", data.accent_color);
        } else {
          root.style.removeProperty("--accent");
          root.style.removeProperty("--accent-hover");
        }
        if (data.primary_color) {
          root.style.setProperty("--ink", data.primary_color);
        } else {
          root.style.removeProperty("--ink");
        }
      }
    } catch {
      setSettings({ org_name: "FLOW Energy App", logo_url: null });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SettingsCtx.Provider value={{ settings, refresh }}>
      {children}
    </SettingsCtx.Provider>
  );
}

export const useSettings = () => useContext(SettingsCtx);

/** Logo component — renders the uploaded logo, falling back to a sun icon. */
export function BrandLogo({ size = 28, className = "" }) {
  const { settings } = useSettings();
  // Org-uploaded logo takes precedence; otherwise fall back to the eneprox wordmark.
  const [theme] = useTheme();
  const src = theme === "dark" ? "/brand/eneprox-logo-dark.png" : (settings?.logo_url || "/brand/eneprox-logo.png");
  return (
    <img
      src={src}
      alt={settings?.org_name || "eneprox Flow"}
      className={className}
      style={{ height: size, width: "auto", objectFit: "contain" }}
    />
  );
}
